import { stripHtml } from '@hooks/useTracking/utils'
import { PageMetadata } from '@hooks/usePageMetadata'

import {
  VideoTrackingParams,
  PlaylistClickTrackingParams,
  PlaylistShowTrackingParams,
  ChapterTrackingParams,
  VideoWidgetImpressionTrackingParams,
  VideoWidgetClickTrackingParams,
  VideoWidgetActionTrackingParams,
} from '@widgets/Video/types/darkcove'
import { ViewportOrientationValues } from '@contexts/viewport'
import { getVideoMainElementTrackingValues } from './utils'
import { TrackingFnType } from '@hooks/useTracking'

type VideoOrientationChangeTrackingParams = VideoTrackingParams & {
  orientation: ViewportOrientationValues
}

type VideoChapterTrackingParams = VideoTrackingParams & ChapterTrackingParams

export const videoImpressionHandler = ({
  extraData,
}: {
  pageMetadata: PageMetadata
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_impression',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'impression',
    // Event specific data
    ...extraData,
  }
}

export const videoPlayHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    // Basic event data
    event: 'video_play_click',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'play_click',
    // Event specific data
    ...extraData,
  }
}

export const videoPlayerSuccessHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_play',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'play',
    // Event specific data
    ...extraData,
  }
}

export const videoBufferingHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_buffering',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'buffering',
    // Event specific data
    ...extraData,
  }
}

export const videoAdRequestHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_ads_ad_request',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'ads_ad_request',
    // Event specific data
    ...extraData,
  }
}

export const videoAdStartedHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_ads_ad_started',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'ads_ad_started',
    // Event specific data
    ...extraData,
  }
}

export const videoAdSkippedHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_ads_ad_skip',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'ads_ad_skip',
    // Event specific data
    ...extraData,
  }
}

export const videoAdErroredHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_ads_ad_error',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'ads_ad_error',
    // Event specific data
    ...extraData,
  }
}

export const videoAdEndedHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_ads_ad_ended',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'ads_ad_end',
    // Event specific data
    ...extraData,
  }
}

export const videoProgressHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_watched',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'watched',
    // Event specific data
    ...extraData,
  }
}

export const videoProgress10SecIntervalHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'video_watched_10',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'watched_10',
    // Event specific data
    ...extraData,
  }
}

export const videoQuartileHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  const { quartileState } = extraData

  return {
    event: `video_${quartileState}`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `video_${quartileState}`,
    // Event specific data
    ...extraData,
  }
}

export const videoEndedHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_end`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `video_end`,
    // Event specific data
    ...extraData,
  }
}

export const videoForwardHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_forward`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `forward`,
    // Event specific data
    ...extraData,
  }
}

export const videoBackwardHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_backward`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `backward`,
    // Event specific data
    ...extraData,
  }
}

export const videoErrorHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_error`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `error`,
    // Event specific data
    ...extraData,
  }
}

export const videoMuteHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_audio`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `mute`,
    // Event specific data
    ...extraData,
  }
}

export const videoUnmuteHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_audio`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `unmute`,
    // Event specific data
    ...extraData,
  }
}

export const videoCaptionsEnableHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_captions`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `enable`,
    // Event specific data
    ...extraData,
  }
}

export const videoCaptionsDisableHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_captions`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `disable`,
    // Event specific data
    ...extraData,
  }
}

export const videoCloseFloatingPlayerHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_close_floatingPlayer`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `close_floatingPlayer`,
    // Event specific data
    ...extraData,
  }
}

export const videoToFloatingPlayerHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    // Basic event data
    event: `video_to_floating`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `to_floating`,
    // Event specific data
    ...extraData,
  }
}

export const videoOverlayEnterHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_to_overlay`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `to_overlay`,
    // Event specific data
    ...extraData,
  }
}

export const videoOverlayExitHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: `video_close_overlay`,
    eventCategory: 'video',
    eventValue: 1,
    eventAction: `close_overlay`,
    // Event specific data
    ...extraData,
  }
}

export const videoOrientationChangeHandler = ({
  extraData,
}: {
  extraData: VideoOrientationChangeTrackingParams
}) => {
  return {
    event: 'video_orientation_change',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'video_orientation_change',
    // Event specific data
    ...extraData,
  }
}

export const videoPlaylistShowHandler = ({
  pageMetadata,
  extraData,
}: {
  pageMetadata: PageMetadata
  extraData: PlaylistShowTrackingParams
}) => {
  return {
    event: 'playlist_show',
    eventCategory: 'Playlist',
    eventValue: 1,
    eventAction: 'playlist_show',
    eventLabel: extraData.playlistTitle,
    // Video specific data (playlist)
    ...getVideoMainElementTrackingValues(pageMetadata),
  }
}

export const videoPlaylistClickHandler: TrackingFnType<
  PlaylistClickTrackingParams
> = ({ pageMetadata, extraData }) => {
  const { articleId } = extraData

  return {
    event: 'playlist_click',
    eventCategory: 'Playlist',
    eventValue: 1,
    eventAction: 'playlist_click',
    eventLabel: articleId,
    // Video specific data (playlist)
    ...getVideoMainElementTrackingValues(pageMetadata),
  }
}

export const videoWidgetImpressionHandler: TrackingFnType<
  VideoWidgetImpressionTrackingParams
> = ({ extraData }) => {
  const { videoId, videoTitle, playerType, indexPosition, videoState } =
    extraData

  return {
    event: 'video_widget_impression',
    eventCategory: 'widget recommendations',
    eventValue: 1,
    eventAction: `impression_${videoState}`,
    eventLabel: indexPosition,
    // Video specific data
    videoTitle: stripHtml(videoTitle),
    videoId: videoId,
    playerType: playerType,
  }
}

export const videoWidgetClickHandler: TrackingFnType<
  VideoWidgetClickTrackingParams
> = ({ extraData }) => {
  const { videoId, videoTitle, playerType, indexPosition, videoState } =
    extraData

  return {
    event: 'video_widget_click',
    eventCategory: 'widget recommendations',
    eventValue: 1,
    eventAction: `click_${videoState}`,
    eventLabel: indexPosition,
    // Video specific data
    videoTitle: stripHtml(videoTitle),
    videoId: videoId,
    playerType: playerType,
  }
}

export const videoWidgetActionHandler: TrackingFnType<
  VideoWidgetActionTrackingParams
> = ({ extraData }) => {
  const { videoId, playerType, widgetAction, videoTitle } = extraData

  return {
    // Basic event data
    event: 'video_widget_action',
    eventCategory: 'widget recommendations',
    eventValue: 1,
    eventAction: widgetAction, // play | replay | close
    // eventLabel: "-",
    // Video specific data
    videoTitle: videoTitle,
    videoId: videoId,
    playerType: playerType,
  }
}

export const videoChapterClickHandler: TrackingFnType<
  VideoChapterTrackingParams
> = ({ extraData }) => {
  return {
    event: 'video_chapterClick',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'video_chapterClick',
    // Event specific data
    ...extraData,
  }
}

export const videoChapterSwipeHandler: TrackingFnType<
  VideoChapterTrackingParams
> = ({ extraData }) => {
  return {
    event: 'video_chapterSwipe',
    eventCategory: 'video_chapters',
    eventValue: 1,
    eventAction: extraData?.buttonLabel ? 'click' : `swipe`,
    // Event specific data
    ...extraData,
  }
}

export const videoChapterButtonClickHandler: TrackingFnType<
  VideoChapterTrackingParams
> = ({ extraData }) => {
  return {
    event: `video_chapterButtonClick`,
    eventCategory: 'video_chapters',
    eventValue: 1,
    eventAction: `video_chapterButtonClick`,
    eventLabel: extraData?.buttonLabel,
    // Event specific data
    ...extraData,
  }
}

export const videoUnmuteCTAButtonClickHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'cta_audio_click',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'audio click',
    // Event specific data
    audioClick: 'unmute',
    ...extraData,
  }
}

export const videoUnmuteCTAButtonImpressionHandler = ({
  extraData,
}: {
  extraData: VideoTrackingParams
}) => {
  return {
    event: 'cta_audio_impression',
    eventCategory: 'video',
    eventValue: 1,
    eventAction: 'audio impression',
    // Event specific data
    audioClick: 'unmute',
    ...extraData,
  }
}

const exports = {
  videoImpressionHandler,
  videoPlayHandler,
  videoPlayerSuccessHandler,
  videoBufferingHandler,
  videoAdRequestHandler,
  videoAdStartedHandler,
  videoAdSkippedHandler,
  videoAdEndedHandler,
  videoAdErroredHandler,
  videoProgressHandler,
  videoProgress10SecIntervalHandler,
  videoQuartileHandler,
  videoEndedHandler,
  videoForwardHandler,
  videoBackwardHandler,
  videoErrorHandler,
  videoMuteHandler,
  videoUnmuteHandler,
  videoCaptionsEnableHandler,
  videoCaptionsDisableHandler,
  videoCloseFloatingPlayerHandler,
  videoToFloatingPlayerHandler,
  videoOverlayEnterHandler,
  videoOverlayExitHandler,
  videoOrientationChangeHandler,
  videoPlaylistShowHandler,
  videoPlaylistClickHandler,
  videoWidgetImpressionHandler,
  videoWidgetClickHandler,
  videoWidgetActionHandler,
  videoChapterClickHandler,
  videoChapterSwipeHandler,
  videoChapterButtonClickHandler,
}

export default exports
